/**
 * Service layer for auth.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, inject, signal } from '@angular/core';

import { API_REPO } from '../../core/services/repositories/api-repo.token';
import { AuthUser, UpdateProfilePayload, UserLanguage } from '../../core/services/repositories/api-repository';
import { I18nService } from '../../core/i18n/i18n.service';
import { TokenService } from '../../core/services/token.service';
import { ThemeService } from '../../core/services/theme.service';

const AUTH_STORAGE_KEY = 'rd.currentUser';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly repo = inject(API_REPO);
  private readonly i18nService = inject(I18nService);
  private readonly tokenService = inject(TokenService);
  private readonly themeService = inject(ThemeService);

  readonly currentUser = signal<AuthUser | null>(null);

  constructor() {
    const persistedUser = this.readPersistedUser();

    if (persistedUser) {
      this.currentUser.set(persistedUser);
      this.i18nService.useLanguage(persistedUser.language ?? 'en');
      if (persistedUser.preferredTheme) {
        this.themeService.setTheme(persistedUser.preferredTheme);
      }
    }
  }

  login(email: string, password: string): Promise<AuthUser | null> {
    return this.repo.login({ email, password });
  }

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.repo.changePassword(currentPassword, newPassword);
  }

  hasStoredSession(): boolean {
    return Boolean(this.tokenService.getAccessToken() || this.tokenService.getRefreshToken());
  }

  async loadProfile(): Promise<void> {
    if (!this.hasStoredSession()) {
      return;
    }

    const profile = await this.repo.getProfile();
    this.upsertCurrentUser({ ...this.currentUser(), ...profile });

    if (profile.preferredTheme) {
      this.themeService.setTheme(profile.preferredTheme);
    }

    if (profile.preferredLanguage) {
      const lang = profile.preferredLanguage.toLowerCase() === 'fr' ? 'fr' : 'en';
      this.i18nService.useLanguage(lang);
    }
  }

  async updateProfile(payload: UpdateProfilePayload): Promise<void> {
    const updated = await this.repo.updateProfile(payload);
    this.upsertCurrentUser({ ...this.currentUser()!, ...updated });

    if (payload.preferredTheme) {
      const theme = payload.preferredTheme === 'dark' ? 'dark' : 'light';
      this.themeService.setTheme(theme);
    }

    if (payload.preferredLanguage) {
      const lang = payload.preferredLanguage.toLowerCase() === 'fr' ? 'fr' : 'en';
      this.i18nService.useLanguage(lang);
    }
  }

  requestSignup(fullName: string, email: string, phone: string, password: string): Promise<void> {
    return this.repo.requestSignup({
      fullName,
      email,
      phone,
      password,
      preferredLanguage: this.i18nService.currentLanguage()
    });
  }

  requestPasswordOtp(email: string): Promise<boolean> {
    return this.repo.requestPasswordOtp(email);
  }

  verifyPasswordOtp(email: string, otp: string): Promise<boolean> {
    return this.repo.verifyPasswordOtp(email, otp);
  }

  resetPassword(email: string, nextPassword: string): Promise<boolean> {
    return this.repo.resetPassword(email, nextPassword);
  }

  checkUserEmailExists(email: string): Promise<{ exists: boolean; message: string }> {
    return this.repo.checkUserEmailExists(email);
  }

  async syncPreferences(): Promise<void> {
    const preferredLanguage = localStorage.getItem('preferredLanguage') ?? undefined;
    const preferredTheme = localStorage.getItem('preferredTheme') ?? undefined;

    if (!preferredLanguage && !preferredTheme) {
      return;
    }

    try {
      await this.repo.updatePreferences({ preferredLanguage, preferredTheme });
      const profile = await this.repo.getProfile();
      this.upsertCurrentUser({ ...this.currentUser()!, ...profile });

      if (profile.preferredTheme) {
        this.themeService.setTheme(profile.preferredTheme);
      }

      if (profile.preferredLanguage) {
        const lang = profile.preferredLanguage.toLowerCase() === 'fr' ? 'fr' : 'en';
        this.i18nService.useLanguage(lang);
      }
    } catch {
      // Non-fatal — local preferences are still applied via ThemeService/i18n
    }
  }

  updateLanguage(language: UserLanguage): void {
    const user = this.currentUser();

    if (!user) {
      return;
    }

    localStorage.setItem('preferredLanguage', language);
    this.upsertCurrentUser({ ...user, language, preferredLanguage: language.toUpperCase() });
  }

  upsertCurrentUser(user: AuthUser): void {
    this.currentUser.set(user);
    this.i18nService.useLanguage(user.language ?? 'en');
    this.persistCurrentUser(user);
  }

  async logout(): Promise<void> {
    const sessionId = this.tokenService.getSessionId();

    try {
      await this.repo.logout(sessionId ?? '');
    } catch {
      // Proceed with local cleanup even if the API call fails
    }

    this.clearSession();
  }

  /** Wipes local auth state immediately without calling the logout API. */
  clearSession(): void {
    this.currentUser.set(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    this.tokenService.clearTokens();
  }

  private persistCurrentUser(user: AuthUser): void {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  }

  private readPersistedUser(): AuthUser | null {
    const rawValue = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as AuthUser;

      if (!parsed || typeof parsed.email !== 'string' || typeof parsed.role !== 'string' || typeof parsed.name !== 'string') {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
      }

      return parsed;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  }
}
